import { useState, useEffect } from 'react';
import { apiRequest, getImageUrl } from '../../utils/api';

const MediaManager = ({ organizationId }) => {
    const [storageInfo, setStorageInfo] = useState(null);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [filter, setFilter] = useState({ type: '', entity_type: '' });
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        loadStorageInfo();
        loadFiles();
    }, [organizationId]);

    useEffect(() => {
        loadFiles();
    }, [filter, pagination.page]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const loadStorageInfo = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/settings/storage`);
            setStorageInfo(response.data);
        } catch (error) {
            console.error('Storage info load error:', error);
        }
    };

    const loadFiles = async () => {
        setLoadingFiles(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...(filter.type && { type: filter.type }),
                ...(filter.entity_type && { entity_type: filter.entity_type })
            });
            
            const response = await apiRequest(`/organizations/${organizationId}/settings/media?${params}`);
            setFiles(response.data.files || []);
            setPagination(prev => ({
                ...prev,
                total: response.data.pagination.total,
                totalPages: response.data.pagination.totalPages
            }));
        } catch (error) {
            console.error('Files load error:', error);
            setFiles([]);
        } finally {
            setLoadingFiles(false);
            setLoading(false);
        }
    };

    const handleDelete = async (fileId) => {
        if (!confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) return;
        
        setDeleting(fileId);
        try {
            await apiRequest(`/organizations/${organizationId}/settings/media/${fileId}`, {
                method: 'DELETE'
            });
            showToast('Dosya başarıyla silindi');
            loadStorageInfo();
            loadFiles();
        } catch (error) {
            showToast(error.message || 'Dosya silinirken hata oluştu', 'error');
        } finally {
            setDeleting(null);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedFiles.length === 0) return;
        if (!confirm(`${selectedFiles.length} dosyayı silmek istediğinizden emin misiniz?`)) return;

        for (const fileId of selectedFiles) {
            try {
                await apiRequest(`/organizations/${organizationId}/settings/media/${fileId}`, {
                    method: 'DELETE'
                });
            } catch (error) {
                console.error('Bulk delete error:', error);
            }
        }
        
        showToast(`${selectedFiles.length} dosya silindi`);
        setSelectedFiles([]);
        loadStorageInfo();
        loadFiles();
    };

    const toggleFileSelection = (fileId) => {
        setSelectedFiles(prev => 
            prev.includes(fileId) 
                ? prev.filter(id => id !== fileId)
                : [...prev, fileId]
        );
    };

    const selectAll = () => {
        if (selectedFiles.length === files.length) {
            setSelectedFiles([]);
        } else {
            setSelectedFiles(files.map(f => f.id));
        }
    };

    const getEntityLabel = (type) => {
        const labels = {
            product: 'Ürün',
            category: 'Kategori',
            banner: 'Banner',
            story: 'Story',
            restaurant: 'Restoran',
            campaign: 'Kampanya'
        };
        return labels[type] || type;
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    const usagePercentage = storageInfo?.percentage || 0;

    return (
        <div className="space-y-6">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
                    toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
                } text-white`}>
                    {toast.message}
                </div>
            )}

            {/* Storage Overview */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white">
                <h3 className="text-lg font-semibold mb-4">💾 Depolama Kullanımı</h3>
                
                {/* Progress Bar */}
                <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                        <span>Kullanılan: {storageInfo?.formatted?.used || '0 B'}</span>
                        <span>Limit: {storageInfo?.formatted?.limit || '1 GB'}</span>
                    </div>
                    <div className="h-4 bg-white/20 rounded-full overflow-hidden">
                        <div 
                            className={`h-full rounded-full transition-all duration-1000 ease-out ${
                                usagePercentage > 90 ? 'bg-red-300' :
                                usagePercentage > 70 ? 'bg-yellow-300' : 'bg-white'
                            }`}
                            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                        />
                    </div>
                    <div className="text-center mt-2 text-2xl font-bold">
                        {usagePercentage.toFixed(1)}%
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-white/10 rounded-lg p-3">
                        <div className="text-2xl font-bold">{storageInfo?.formatted?.used || '0 B'}</div>
                        <div className="text-xs opacity-80">Kullanılan</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                        <div className="text-2xl font-bold">{storageInfo?.formatted?.remaining || '1 GB'}</div>
                        <div className="text-xs opacity-80">Kalan</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                        <div className="text-2xl font-bold">{pagination.total}</div>
                        <div className="text-xs opacity-80">Dosya</div>
                    </div>
                </div>

                {/* Breakdown */}
                {storageInfo?.breakdown?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/20">
                        <div className="text-sm font-medium mb-2">Dosya Dağılımı</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {storageInfo.breakdown.map(item => (
                                <div key={item.entity_type} className="bg-white/10 rounded px-3 py-2 text-sm">
                                    <span className="font-medium">{getEntityLabel(item.entity_type)}</span>
                                    <span className="ml-2 opacity-80">{item.file_count} dosya</span>
                                    <div className="text-xs opacity-60">{item.formatted_size}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="bg-gray-800 rounded-xl p-4">
                <div className="flex flex-wrap gap-4 items-center">
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Dosya Tipi</label>
                        <select
                            value={filter.type}
                            onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value }))}
                            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        >
                            <option value="">Tümü</option>
                            <option value="image">Görseller</option>
                            <option value="video">Videolar</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Kategori</label>
                        <select
                            value={filter.entity_type}
                            onChange={(e) => setFilter(prev => ({ ...prev, entity_type: e.target.value }))}
                            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        >
                            <option value="">Tümü</option>
                            <option value="product">Ürünler</option>
                            <option value="category">Kategoriler</option>
                            <option value="banner">Bannerlar</option>
                            <option value="story">Storyler</option>
                            <option value="restaurant">Restoran</option>
                            <option value="campaign">Kampanyalar</option>
                        </select>
                    </div>
                    
                    {selectedFiles.length > 0 && (
                        <div className="ml-auto">
                            <button
                                onClick={handleBulkDelete}
                                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                            >
                                🗑️ {selectedFiles.length} dosya sil
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Files Grid */}
            <div className="bg-gray-800 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-700 flex items-center">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={selectedFiles.length === files.length && files.length > 0}
                            onChange={selectAll}
                            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-orange-500"
                        />
                        <span className="ml-2 text-sm text-gray-400">Tümünü Seç</span>
                    </label>
                    <span className="ml-auto text-sm text-gray-400">
                        {pagination.total} dosya
                    </span>
                </div>

                {loadingFiles ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                    </div>
                ) : files.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        <div className="text-4xl mb-2">📁</div>
                        <div>Henüz dosya yok</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4">
                        {files.map(file => (
                            <div 
                                key={file.id}
                                className={`relative group rounded-lg overflow-hidden bg-gray-700 ${
                                    selectedFiles.includes(file.id) ? 'ring-2 ring-orange-500' : ''
                                }`}
                            >
                                {/* Checkbox */}
                                <div className="absolute top-2 left-2 z-10">
                                    <input
                                        type="checkbox"
                                        checked={selectedFiles.includes(file.id)}
                                        onChange={() => toggleFileSelection(file.id)}
                                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-orange-500"
                                    />
                                </div>

                                {/* Preview */}
                                <div className="aspect-square bg-gray-900">
                                    {file.file_type === 'video' ? (
                                        <div className="w-full h-full flex items-center justify-center text-4xl">
                                            🎬
                                        </div>
                                    ) : (
                                        <img 
                                            src={getImageUrl(file.url || file.file_path)}
                                            alt={file.filename}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23374151" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%239ca3af" font-size="40">📷</text></svg>';
                                            }}
                                        />
                                    )}
                                </div>

                                {/* Info */}
                                <div className="p-2">
                                    <div className="text-xs text-white truncate" title={file.filename}>
                                        {file.filename}
                                    </div>
                                    <div className="flex justify-between items-center mt-1">
                                        <span className="text-xs text-gray-400">
                                            {file.formatted_size}
                                        </span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                            file.file_type === 'video' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                                        }`}>
                                            {file.file_type === 'video' ? 'Video' : 'Görsel'}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        {getEntityLabel(file.entity_type)}
                                    </div>
                                </div>

                                {/* Delete button */}
                                <button
                                    onClick={() => handleDelete(file.id)}
                                    disabled={deleting === file.id}
                                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    {deleting === file.id ? (
                                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                                    ) : (
                                        <span className="text-sm">🗑️</span>
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="px-4 py-3 border-t border-gray-700 flex justify-center gap-2">
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                            className="px-3 py-1 rounded bg-gray-700 text-white disabled:opacity-50"
                        >
                            ←
                        </button>
                        <span className="px-3 py-1 text-gray-400">
                            {pagination.page} / {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page === pagination.totalPages}
                            className="px-3 py-1 rounded bg-gray-700 text-white disabled:opacity-50"
                        >
                            →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MediaManager;

